import { WorkArtefactCreatedCard } from '@/components/ui/work-tool-cards/WorkArtefactCreatedCard';
import { artifactFromToolUse, ExportGeneratingCard } from '@heidi/agents';
import type { ToolUseItem } from '@heidi/agents/contracts';
import type { Artifact } from '@heidi/agents/data';
import { ArtefactBlock } from '@heidi/evidence';
import type { ArtefactType } from '@heidi/evidence/contracts';
import type { IntlShape, MessageDescriptor } from 'react-intl';

import { toExportArtefactKind } from '@/features/evidence/adapters/to-export-artefact-kind';
import { readToolResultRecord } from '@heidi/agents/contracts';
import { toArtefactStatus, toCreateTemplateStatus } from '../status';
import { readString, readToolData } from './readers';

/** Normalized fields a per-tool reader extracts from `provider_metadata.data`. */
export interface ArtefactPayload {
  url?: string;
  title?: string;
  filename?: string;
  /** Slide Builder id (`presentation_id`) — pptx only. */
  presentationId?: string;
}

interface ArtefactToolConfig {
  artefactType: ArtefactType;
  /**
   * Localized title shown when the result carries neither `title` nor
   * `filename` — passed through `intl.formatMessage` so non-English locales
   * don't see English (the per-tool reader supplies the real title when present).
   */
  titleFallback: MessageDescriptor;
  readPayload: (item: ToolUseItem) => ArtefactPayload;
  /**
   * Optional preview wiring. Receives the extracted payload; return a click
   * handler to make the card a preview affordance, or `undefined` to keep
   * the default behaviour (card opens the export URL in a new tab).
   * Used by pptx to open the Slide Builder artifact panel.
   */
  buildOnPreview?: (payload: ArtefactPayload) => (() => void) | undefined;
}

/**
 * Shared renderer for the direct export/generate tools that surface as an
 * `ArtefactBlock` ("Preview / Download" card). Each tool passes its
 * `artefactType`, a `titleFallback`, and a `readPayload` that pulls
 * `{ url, title, ... }` off `provider_metadata.data`.
 *
 * By default no `onPreview` is passed and the card opens its durable export
 * URL in a new tab. A tool can opt into an in-canvas preview via
 * `buildOnPreview` (pptx → the Slide Builder artifact panel).
 *
 * Only the completed state (with a URL) keeps the rich card; every other status
 * returns `null` so the tool falls through to the inline generic row.
 */
export const renderArtefactToolUse = (
  item: ToolUseItem,
  { artefactType, titleFallback, readPayload, buildOnPreview }: ArtefactToolConfig,
  intl: IntlShape
): JSX.Element | null => {
  const status = toArtefactStatus(item.status);
  const payload = readPayload(item);
  const { url, title } = payload;

  // Show an animated "preparing" card while the export runs, instead of
  // falling through to the blank generic row.
  if (status === 'generating') {
    // The one wire join in this cluster: `artefactType` here is the evidence
    // door's `ArtefactType`, and the card renders against the agents union. The
    // adapter names both, so a member added to either side is a compile error in
    // it rather than a silent pass at this line.
    return (
      <ExportGeneratingCard
        artefactType={toExportArtefactKind(artefactType)}
        title={title ?? intl.formatMessage(titleFallback)}
      />
    );
  }

  if (status !== 'completed') return null;
  if (!url) return null;

  return (
    <ArtefactBlock
      artefactType={artefactType}
      status="completed"
      title={title ?? intl.formatMessage(titleFallback)}
      url={url}
      onPreview={buildOnPreview?.(payload)}
    />
  );
};

const readWorkArtefactPayload = (item: ToolUseItem) => {
  const data = readToolData(item) ?? readToolResultRecord(item);
  return {
    filename: readString(data, 'filename'),
    collectionName: readString(data, 'collection_name'),
  };
};

export const renderSaveWorkOutputToolUse = (
  item: ToolUseItem,
  intl: IntlShape,
  onOpenArtifact?: (artifact: Artifact) => void
): JSX.Element | null => {
  const status = toCreateTemplateStatus(item.status);
  if (status !== 'completed') return null;

  const { filename, collectionName } = readWorkArtefactPayload(item);
  const title =
    filename ?? intl.formatMessage({ defaultMessage: 'Artefact', id: 'W7Vzo86RxX' });
  const artifact = artifactFromToolUse(item);

  return (
    <WorkArtefactCreatedCard
      title={title}
      subtitle={collectionName}
      onOpen={artifact && onOpenArtifact ? () => onOpenArtifact(artifact) : undefined}
    />
  );
};
